import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { VipPlan } from '../types';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { formatDate, parseServerDate } from '../utils/date';

import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Clock } from 'lucide-react';

const VipPlanPage: React.FC<{ isDashboard?: boolean }> = ({ isDashboard = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [plans, setPlans] = useState<VipPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.vip.getActivePlans();
      if (response.success && response.data) {
        setPlans(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load VIP plans:', error);
      showAlert({
        type: 'error',
        message: error?.response?.data?.message || t('vipPlan.errorLoadPlans'),
      });
    } finally {
      setLoading(false);
    }
  }, [showAlert, t]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSelectPlan = async (plan: VipPlan) => {
    if (!user) {
      showAlert({
        type: 'warning',
        message: t('vipPlan.warningLogin'),
      });
      return;
    }
    if (user.role !== 'EMPLOYER') {
      showAlert({
        type: 'warning',
        message: t('vipPlan.warningEmployer'),
      });
      return;
    }
    
    // 檢查用戶是否已經是 VIP 且未過期
    if (user.isVip && user.vipExpiryDate) {
      const expiryDate = parseServerDate(user.vipExpiryDate);
      const now = new Date();
      if (expiryDate > now) {
        showAlert({
          type: 'info',
          message: t('vipPlan.infoVipActive', { date: formatDate(expiryDate) }),
        });
        return;
      }
    }
    
    // 檢查是否有未完成的支付
    try {
      const response = await apiService.payments.getUserPayments(user.id);
      if (response.success && response.data) {
        const pendingPayments = response.data.filter(
          (payment: any) => payment.status === 'PENDING' || payment.status === 'PROCESSING'
        );
        
        if (pendingPayments.length > 0) {
          showAlert({
            type: 'warning',
            message: t('vipPlan.warningPendingPayment'),
          });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking pending payments:', error);
    }
    
    navigate(`/payment?planId=${plan.id}`);
  };

  // Pick locale-aware field from plan
  const localeField = (plan: VipPlan, field: 'name' | 'description' | 'features'): string => {
    const lang = i18n.language || 'zh-HK';
    const suffix = lang.startsWith('zh-CN') ? 'ZhCN' : lang.startsWith('en') ? 'En' : 'ZhHK';
    const key = `${field}${suffix}` as keyof VipPlan;
    return (plan[key] as string | undefined) || '';
  };

  const planFeatures = (plan: VipPlan): string[] => {
    const raw = localeField(plan, 'features');
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  };

  const formatPrice = (price: number, currency: string) => {
    const symbol = currency === 'HKD' ? 'HK$' : currency === 'USD' ? 'US$' : '$';
    return `${symbol}${price.toLocaleString()}`;
  };

  const formatDuration = (days: number) => {
    if (days >= 365) {
      const years = Math.floor(days / 365);
      return t('vipPlan.durationYears', { count: years });
    } else if (days >= 30) {
      const months = Math.floor(days / 30);
      return t('vipPlan.durationMonths', { count: months });
    } else {
      return t('vipPlan.durationDays', { count: days });
    }
  };

  // 檢查用戶是否已經是 VIP 且未過期
  const isVipActive = Boolean(
    user?.isVip && 
    user.vipExpiryDate && 
    parseServerDate(user.vipExpiryDate) > new Date()
  );

  if (loading) {
    return <Loading fullScreen message={t('common.loading')} />;
  }

  const inner = (
    <>
      <div className="mb-8">
        {plans.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <p className="text-muted-foreground text-lg">{t('vipPlan.noPlans')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan) => {
              const isRecommended = plan.isRecommended;
              const displayName = localeField(plan, 'name') || plan.name;
              const displayDesc = localeField(plan, 'description') || plan.description || '';
              const features = planFeatures(plan);

              return (
                <Card 
                  key={plan.id} 
                  className={`relative flex flex-col text-center overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${
                    isRecommended ? 'border-primary ring-2 ring-primary shadow-lg' : 'border-border'
                  }`}
                >
                  {/* Top color bar */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-primary`} />
                  
                  {isRecommended && (
                    <div className="absolute top-4 -right-10 bg-primary text-primary-foreground text-xs font-bold py-1 px-10 rotate-45">
                      {t('vipPlan.popular')}
                    </div>
                  )}

                  <CardHeader className="pb-4 mt-4">
                    <CardTitle className="text-2xl font-bold text-foreground mb-1">
                      {displayName}
                    </CardTitle>
                    <p className="text-muted-foreground font-medium">
                      {formatDuration(plan.durationDays)}
                    </p>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col">
                    <div className="mb-6">
                      <span className="text-4xl font-extrabold text-primary">
                        {formatPrice(plan.price, plan.currency)}
                      </span>
                    </div>

                    {displayDesc && (
                      <div className="mb-6 p-4 bg-muted rounded-lg text-sm text-muted-foreground leading-relaxed text-left">
                        {displayDesc}
                      </div>
                    )}

                    {features.length > 0 && (
                      <div className="text-left mt-auto">
                        <ul className="space-y-3">
                          {features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-foreground pb-2 border-b border-border last:border-0">
                              <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                                <Check className="w-3.5 h-3.5" />
                              </div>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>

                  <CardFooter className="pt-4 pb-6 px-6">
                    <Button
                      onClick={() => handleSelectPlan(plan)}
                      disabled={isVipActive}
                      variant={isVipActive ? 'outline' : isRecommended ? 'default' : 'outline'}
                      size="lg"
                      className={`w-full ${!isVipActive && isRecommended ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-md' : ''}`}
                    >
                      {isVipActive ? t('vipPlan.currentVip') : t('vipPlan.selectPlan')}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {user?.isVip && user.vipExpiryDate && (
        <Card className="bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 shadow-md overflow-hidden relative">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-amber-400" />
          <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-amber-100 dark:border-amber-900">
            <div className="flex items-center gap-3">
              <Crown className="w-7 h-7 text-amber-600 dark:text-amber-500" />
              <CardTitle className="text-xl font-bold text-amber-900 dark:text-amber-50 m-0">{t('vipPlan.statusTitle')}</CardTitle>
            </div>
            <Badge 
              variant="outline" 
              className={`px-4 py-1.5 text-sm font-bold border-0 ${
                isVipActive ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100' : 'bg-destructive/10 text-destructive'
              }`}
            >
              VIP
            </Badge>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-amber-800/80 dark:text-amber-200/80">{t('vipPlan.status')}:</span>
                <span className={`text-lg font-bold ${isVipActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                  {(() => {
                    const expiryDate = parseServerDate(user.vipExpiryDate);
                    const now = new Date();
                    return expiryDate > now ? t('vipPlan.valid') : t('vipPlan.expired');
                  })()}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-amber-800/80 dark:text-amber-200/80">{t('vipPlan.expiryDate')}:</span>
                <span className="text-lg font-bold text-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  {formatDate(user.vipExpiryDate)}
                </span>
              </div>
              {(() => {
                const expiryDate = parseServerDate(user.vipExpiryDate);
                const now = new Date();
                const daysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                
                if (daysRemaining > 0) {
                  return (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-amber-800/80 dark:text-amber-200/80">{t('vipPlan.daysRemaining')}:</span>
                      <span className="text-lg font-bold text-foreground">{daysRemaining} {t('vipPlan.days')}</span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            
            {isVipActive && (
              <div className="mt-6 pt-4 border-t border-amber-100 dark:border-amber-900 text-center">
                <p className="text-sm font-medium text-amber-800/80 dark:text-amber-200/80">
                  {t('vipPlan.renewalNote')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );

  if (isDashboard) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">{t('vipPlan.title')}</CardTitle>
            {plans.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {isVipActive ? t('vipPlan.subtitle') : t('vipPlan.subtitleNoVip', { count: plans.length })}
              </p>
            )}
          </CardHeader>
          <CardContent>{inner}</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen text-foreground p-4 sm:p-8">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">{t('vipPlan.title')}</h1>
          {plans.length > 0 && (
            <p className="text-muted-foreground text-sm mt-1">
              {isVipActive ? t('vipPlan.subtitle') : t('vipPlan.subtitleNoVip', { count: plans.length })}
            </p>
          )}
        </div>
        {inner}
      </div>
    </div>
  );
};

export default VipPlanPage;
